import { AutomatedContentBanner } from "@2u-landing-pages/components/AutomatedContent";
import {
  Accordion,
  Banner,
  Bento,
  Carousel,
  Flex,
  Form,
  Gallery,
  Hero,
  Marquee,
  Split,
} from "@2u-landing-pages/templates";
import { DynamicExperienceFlow } from "@2u-landing-pages/templates/DynamicExperienceFlow";
import {
  isTypeBanner,
  TypeAccordionWithoutUnresolvableLinksResponse,
  TypeAutomated_contentWithoutUnresolvableLinksResponse,
  TypeBannerWithoutUnresolvableLinksResponse,
  TypeBentoWithoutUnresolvableLinksResponse,
  TypeCarouselWithoutUnresolvableLinksResponse,
  TypeCategory_variationWithoutUnresolvableLinksResponse,
  TypeDynamic_experience_flowWithoutUnresolvableLinksResponse,
  TypeFlexWithoutUnresolvableLinksResponse,
  TypeFormWithoutUnresolvableLinksResponse,
  TypeGalleryWithoutUnresolvableLinksResponse,
  TypeHeroWithoutUnresolvableLinksResponse,
  TypeMarqueeWithoutUnresolvableLinksResponse,
  TypeSplitWithoutUnresolvableLinksResponse,
  TypeSticky_elementWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useIntersectionObserver } from "@2u-landing-pages/utils";
import EventClient from "@2u-landing-pages/utils/event-client";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import React, { useCallback } from "react";

type Section =
  | TypeAccordionWithoutUnresolvableLinksResponse
  | TypeFormWithoutUnresolvableLinksResponse
  | TypeHeroWithoutUnresolvableLinksResponse
  | TypeBannerWithoutUnresolvableLinksResponse
  | TypeBentoWithoutUnresolvableLinksResponse
  | TypeSplitWithoutUnresolvableLinksResponse
  | TypeGalleryWithoutUnresolvableLinksResponse
  | TypeCarouselWithoutUnresolvableLinksResponse
  | TypeMarqueeWithoutUnresolvableLinksResponse
  | TypeSticky_elementWithoutUnresolvableLinksResponse
  | TypeFlexWithoutUnresolvableLinksResponse
  | TypeAutomated_contentWithoutUnresolvableLinksResponse
  | TypeDynamic_experience_flowWithoutUnresolvableLinksResponse;

interface ComponentRendererProps {
  section: Section;
  sectionName: string;
  inspector: (props: { entryId: string; fieldId: string }) => any;
  isLoading: boolean;
  lob?: "execed" | "degree";
  category?: string;
  mode: "multi-program" | "single-program";
}

const ComponentRenderer: React.FC<ComponentRendererProps> = ({ section, ...rest }) => {
  const id = section?.sys?.id;

  switch (section?.sys?.contentType?.sys?.id) {
    case "hero":
      return (
        <Hero
          id={id}
          {...(section as TypeHeroWithoutUnresolvableLinksResponse).fields}
          {...rest}
          programLogo={undefined}
          isLoading={false}
        />
      );
    case "automated_content":
      return (
        <AutomatedContentBanner
          id={id}
          {...(section as TypeAutomated_contentWithoutUnresolvableLinksResponse).fields}
          {...rest}
        />
      );
    case "banner":
      return <Banner id={id} {...(section as TypeBannerWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "bento":
      return <Bento id={id} {...(section as TypeBentoWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "split":
      return <Split id={id} {...(section as TypeSplitWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "gallery":
      return <Gallery id={id} {...(section as TypeGalleryWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "carousel":
      return <Carousel id={id} {...(section as TypeCarouselWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "accordion":
      return <Accordion id={id} {...(section as TypeAccordionWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "form":
      return <Form id={id} {...(section as TypeFormWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "marquee":
      return <Marquee id={id} {...(section as TypeMarqueeWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "flex":
      return <Flex id={id} {...(section as TypeFlexWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "dynamic_experience_flow":
      return <DynamicExperienceFlow {...(section as TypeDynamic_experience_flowWithoutUnresolvableLinksResponse).fields} {...rest} />;
    default:
      return null;
  }
};

interface SectionsProps {
  sections: TypeCategory_variationWithoutUnresolvableLinksResponse["fields"]["sections"];
  isLoading?: boolean;
  lob?: "execed" | "degree";
  category?: string;
  mode?: "multi-program" | "single-program";
}

export const Sections: React.FC<SectionsProps> = ({ sections, isLoading = false, lob, category }) => {
  const inspector = useContentfulInspectorMode();

  const handleIntersect = useCallback((target: HTMLElement) => {
    const { position } = target.dataset;

    const heading = target.querySelector("[class*='sectionHeading']");
    const headingText = heading?.textContent || "";

    EventClient.elementViewed({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: `Section ${position}`,
        text: headingText,
      },
    });
  }, []);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });

  return (
    <>
      {sections?.map((section, index) => {
        if (!section) return null;

        if (!section?.sys?.contentType?.sys?.id) return null;

        const style = {} as React.CSSProperties;
        if (isTypeBanner(section) && section?.fields?.sticky) {
          style.position = "sticky";
          style.top = "0";
          style.zIndex = 100;
        }

        return (
          <div
            key={section.sys.id}
            id={section.fields.section_id || section.sys.id}
            data-name={section.fields.name}
            data-position={index + 1}
            ref={(element) => {
              if (element) addObserver(element);
            }}
            style={style}
          >
            <ComponentRenderer
              section={section as Section}
              sectionName={section.fields.name}
              inspector={inspector}
              isLoading={isLoading}
              lob={lob}
              category={category}
              mode="multi-program"
            />
          </div>
        );
      })}
    </>
  );
};
